import * as React from "react";
import {
  Button,
  Input,
  Spinner,
  Tab,
  TabList,
  TabValue,
  Text,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  Search20Regular,
  LockClosed20Filled,
  Dismiss20Regular,
  CheckmarkCircle24Regular,
  Checkmark24Regular,
  Checkmark24Filled,
  Video20Filled,
  Video24Filled,
} from "@fluentui/react-icons";
import { createClient, Room, MatrixClient, RoomType } from "matrix-js-sdk";
import { MatrixCreds } from "./SignIn";
import { ClientEvent } from "matrix-js-sdk";
import { addJitsiWidgetLite } from "../utils/addJitsiWidgetLite";

const useStyles = makeStyles({
  container: {
    padding: tokens.spacingHorizontalL,
    minHeight: "460px",
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  tabs: { marginTop: tokens.spacingVerticalL, width: "100%", display: "flex" },
  tab: { flex: 1, minWidth: 0, textAlign: "center" },
  tabPanel: { marginTop: tokens.spacingVerticalL, flex: 1 },
  searchRow: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalS,
    position: "relative",
  },
  dropdownList: {
    position: "absolute",
    left: 0,
    right: 0,
    zIndex: 10,
    maxHeight: "220px",
    overflowY: "auto",
    background: "#fff",
    border: `1px solid ${tokens.colorNeutralStroke3}`,
    borderRadius: "8px",
    marginTop: "2px",
    boxShadow: "0 6px 24px rgba(0,0,0,0.13)",
    animation: "fadeIn 0.18s",
  },
  "@keyframes fadeIn": {
    "0%": { opacity: 0, transform: "translateY(-10px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },
  option: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
    transition: "background 0.15s",
    padding: "10px 14px",
    cursor: "pointer",
    fontSize: "15px",
    "&:hover": {
      background: tokens.colorNeutralBackground3,
    },
  },
  selectedOption: {
    background: "#e6f0fa",
    color: "#3178c6",
    fontWeight: 700,
  },
  orText: { marginRight: "8px", fontSize: "14px", minWidth: "22px" },
  plusBtn: { minWidth: "40px", height: "36px", padding: 0 },

  helperText: {
    color: tokens.colorNeutralForeground3,
    fontSize: "13px",
    marginTop: "2px",
  },
  infoBlock: {
    margin: "10px 0 16px 0",
    fontSize: "14px",
    color: tokens.colorNeutralForeground3,
    fontStyle: "italic",
    background: "#f5faff",
    borderRadius: "8px",
    padding: "13px 18px 10px 18px",
    lineHeight: 1.5,
    borderLeft: `3px solid #4d88bc`,
  },
  infoTitle: {
    fontWeight: 600,
    fontStyle: "normal",
    color: "#222",
    marginBottom: "6px",
    display: "block",
    fontSize: "15px",
  },
  footer: {
    marginTop: "auto",
    display: "flex",
    alignItems: "center",
    borderTop: `1px solid ${tokens.colorNeutralStroke3}`,
    paddingTop: tokens.spacingVerticalM,
    fontSize: "12px",
    color: tokens.colorNeutralForeground3,
    justifyContent: "space-between",
  },
  signOut: {
    color: tokens.colorBrandForegroundLink,
    cursor: "pointer",
    marginLeft: tokens.spacingHorizontalL,
    textDecoration: "underline",
    fontWeight: 500,
  },
  user: { display: "flex", alignItems: "center", gap: "6px" },
  gear: { fontSize: "15px", marginRight: "4px" },
  tabIcon: {
    display: "block",
    margin: "0 auto 4px auto",
    width: "24px",
    height: "24px",
  },
  selectedBanner: {
    padding: "5px 15px",
    background: "#e6f0fa",
    borderRadius: "8px",
    margin: "12px 0 0 0",
    textAlign: "left",
    fontSize: "15px",
  },
  createLabel: {
    fontWeight: 500,
    fontSize: "15px",
    marginTop: "17px",
    marginBottom: "7px",
    display: "block",
    letterSpacing: "-0.5px",
  },
  createRow: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    marginBottom: "2px",
    gap: "0px",
    position: "relative",
  },
  wideInput: {
    flex: 1,
    minWidth: "0",
    borderRadius: "px",
    border: "1px solid #B4B4B4",
    background: "#fff",
  },
  plusBtnCircle: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    background: "#1777ab", // Stronger blue!
    marginLeft: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    transition: "background 0.18s",
    cursor: "pointer",
    "&:hover": {
      background: "#146794", // Darker blue on hover
    },
    "&:active": {
      background: "#146794", // Even deeper blue on click
    },
  },
  plusIcon: {
    color: "#fff",
    fontSize: "30px",
    fontWeight: 500,
    lineHeight: 1,
    display: "block",
    marginTop: "-1px",
  },
});

interface Props {
  creds: MatrixCreds;
  onSignOut: () => void;
  onRoomLinkSelected?: (params: {
    publicLink: string;
    organizerLink?: string;
    roomId?: string;
    isFromExisting?: boolean;
    roomName?: string;
  }) => void;
  meetingTitle: string;
}

export default function MeetingTabs({
  creds,
  onSignOut,
  onRoomLinkSelected,
  meetingTitle,
}: Props): JSX.Element {
  const css = useStyles();
  const [tab, setTab] = React.useState<TabValue>("internal");
  const [selectedRoom, setSelectedRoom] = React.useState<string | null>(null);
  const [internalRooms, setInternalRooms] = React.useState<Room[]>([]);
  const [externalRooms, setExternalRooms] = React.useState<Room[]>([]);
  const [search, setSearch] = React.useState("");
  const [newRoom, setNewRoom] = React.useState("");
  const [isEditing, setIsEditing] = React.useState(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const [inputHeight, setInputHeight] = React.useState(44);
  const [creating, setCreating] = React.useState(false);
  const [selectedRoomSource, setSelectedRoomSource] = React.useState<"existing" | "created" | null>(
    null
  );

  const userEmail = creds.userId;

  // --- Room permalink function (with external param logic) ---
  function getRoomPermalink(roomId: string, isExternal: boolean): string {
    const permalinkPrefix =
      creds.wellKnown?.["config.client.salax"]?.permalink_prefix ||
      creds.wellKnown?.permalink_prefix;
    let base;
    if (permalinkPrefix) {
      base = `${permalinkPrefix.replace(/\/+$/, "")}/#/${roomId}`;
    } else if (creds.webClientUrl) {
      base = `${creds.webClientUrl.replace(/\/+$/, "")}/#/${roomId}`;
    } else {
      base = `https://salax.ssh.net/#/${roomId}`;
    }
    if (isExternal) {
      const afterHash = base.split("#/")[1] || "";
      if (afterHash.includes("?")) {
        base += "&external=true";
      } else {
        base += "?external=true";
      }
    }
    return base;
  }

  async function handleCreateRoom() {
    if (!newRoom.trim()) return;

    // reset UI selection
    setSelectedRoom(null);
    setSelectedRoomSource(null);
    onRoomLinkSelected?.({ publicLink: "", organizerLink: "", roomId: "", roomName: "" });
    setCreating(true);

    let done = false;
    const client = createClient({
      baseUrl: creds.homeserverUrl,
      accessToken: creds.accessToken,
      userId: creds.userId,
      deviceId: creds.deviceId,
    });

    try {
      await client.startClient({ initialSyncLimit: 1 });

      /* ---------- 1. build create-room opts ---------- */
      const internal = tab === "internal";
      const roomOptions = {
        name: newRoom,
        preset: internal ? "public_chat" : "private_chat",
        visibility: internal ? "public" : "private",
        creation_content: {
          type: internal ? RoomType.ElementVideo : RoomType.ExternalVideoCall,
        },
        /* give ourselves PL 200 temporarily so we may send the widget event */
        power_level_content_override: {
          users: { [creds.userId]: 200 },
        },
      };

      /* ---------- 2. create the room ---------- */
      const { room_id } = await client.createRoom(roomOptions);

      /* wait until the room object is in the store */
      const room = await new Promise<Room>((resolve) => {
        const fromCache = client.getRoom(room_id);
        if (fromCache) return resolve(fromCache);
        const onRoom = (r: Room) => {
          if (r.roomId === room_id) {
            client.removeListener("Room", onRoom);
            resolve(r);
          }
        };
        client.on("Room", onRoom);
      });

      /* ---------- 3. add the Jitsi widget ---------- */
      await addJitsiWidgetLite({
        client,
        roomId: room_id,
        roomName: newRoom,
        jitsiDomain: "call.ssh.sshcollabx.net",
        useJwt: true,
        isVideoChannel: true,
        ifExternalMeeting: !internal,
      });

      /* lock the widget: drop our PL back to 100 (admin) */
      await client.setPowerLevel(room_id, creds.userId, 100);

      /* ---------- 4. update local state ---------- */
      (internal ? setInternalRooms : setExternalRooms)((prev) => [
        ...prev.filter((r) => r.roomId !== room_id),
        room,
      ]);

      setNewRoom("");
      setSelectedRoom(room_id);
      setSelectedRoomSource("created");

      const publicLink = getRoomPermalink(room_id, !internal);
      const organizerLink = internal ? undefined : getRoomPermalink(room_id, false);
      onRoomLinkSelected?.({ publicLink, organizerLink, roomId: room_id, roomName: room.name });
    } catch (e) {
      console.error("handleCreateRoom failed:", e);
    } finally {
      if (!done) setCreating(false);
      try {
        client.stopClient();
      } catch {
        /* ignore */
      }
    }
  }

  function renderRoomIcon(room: Room | undefined): React.ReactNode {
    if (!room) return null;
    if (room.getJoinRule && room.getJoinRule() === "public") {
      return (
        <span
          style={{
            fontSize: 22,
            width: 24,
            height: 24,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "inherit",
            flex: "0 0 auto",
            fontWeight: 700,
            opacity: 0.82,
          }}
          aria-label="Public"
        >
          #
        </span>
      );
    }
    return (
      <LockClosed20Filled
        style={{
          color: "inherit",
          width: 24,
          height: 24,
          flex: "0 0 auto",
          opacity: 0.82,
        }}
        aria-label="Private"
      />
    );
  }

  // Info block based on room type (internal tab only)
  function renderInfoBlock(
    room: Room | undefined,
    source: "created" | "existing" = "existing"
  ): React.ReactNode {
    if (!room) return null;

    const isPublic = room.getJoinRule && room.getJoinRule() === "public";

    // Access tab variable in scope (as in your current implementation)
    if (source === "created") {
      // Show custom messages for 'created'
      if (tab === "internal") {
        return (
          <div className={css.infoBlock}>
            <span>
              You created a new video room. The new room will be a <b>public internal video room</b>
              , meaning anyone with access to your home server and the meeting link can join.
              However, it will <b>not</b> appear in the explore public room list.
            </span>
          </div>
        );
      }
      // external tab
      return (
        <div className={css.infoBlock}>
          <span>
            You created a new video room. The new room will be created as a{" "}
            <b>private external video room</b>. You can invite additional meeting organizers from
            SalaX Secure Messaging room details.
          </span>
        </div>
      );
    }

    // Default behavior for 'existing' (selected)
    if (tab === "internal") {
      return (
        <div className={css.infoBlock}>
          {isPublic ? (
            <>
              <span>
                You <b>selected</b> a <b>public room</b>. Participants on the meeting invitation
                will join this room. <br />
                <b>Be aware that they may have access to previous conversations.</b>
              </span>
            </>
          ) : (
            <>
              <span>
                You <b>selected</b> a <b>private room</b>. Make sure all participants have the
                necessary access.
                <br />
                Only room members can join, and room invitations can be sent through your SalaX app.
              </span>
            </>
          )}
        </div>
      );
    }

    // For external tab and 'existing' // (You might want a custom message, or you can just return null)
    return null;
  }

  // Callback ref to measure input height
  const inputRef = React.useCallback((node: HTMLInputElement | null) => {
    if (node !== null) {
      setInputHeight(node.offsetHeight);
    }
  }, []);

  // Keep in sync with meetingTitle unless user editting

  React.useEffect(() => {
    if (!isEditing) {
      setNewRoom(meetingTitle || "");
    }
  }, [meetingTitle, isEditing]);

  const handleNewRoomInputFocus = () => setIsEditing(true);
  const handleNewRoomInputBlur = () => setIsEditing(false);

  const handleNewRoomInputChange = (_: any, data: any) => setNewRoom(data.value);

  // Fetch rooms as before
  React.useEffect(() => {
    if (!creds?.accessToken) return;
    setLoading(true);
    const client: MatrixClient = createClient({
      baseUrl: creds.homeserverUrl,
      accessToken: creds.accessToken,
      userId: creds.userId,
      deviceId: creds.deviceId,
    });
    let isMounted = true;
    client.startClient({ initialSyncLimit: 10 });
    client.once(ClientEvent.Sync, (state: string) => {
      if (!isMounted) return;
      if (["PREPARED", "SYNCING", "CATCHUP"].includes(state)) {
        const allRooms = client.getRooms();

        const mDirect = client.getAccountData("m.direct")?.getContent() || {};
        const dmRoomIds = new Set();
        for (const userId in mDirect) {
          for (const roomId of mDirect[userId]) {
            dmRoomIds.add(roomId);
          }
        }

        const internals: Room[] = [];
        const externals: Room[] = [];
        for (const room of allRooms) {
          if (dmRoomIds.has(room.roomId)) continue; // Skip DMs!

          if (room.isElementExternalVideoRoom()) {
            externals.push(room);
          } else {
            internals.push(room);
          }
        }
        setInternalRooms(internals);
        setExternalRooms(externals);
        setLoading(false);
        client.stopClient();
      }
    });
    return () => {
      isMounted = false;
      try {
        client.stopClient();
      } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creds, tab]);

  // On focus: show dropdown with all rooms
  const handleInputFocus = () => {
    setDropdownOpen(true);
  };

  // On blur: hide dropdown after a little delay, to allow click selection
  const handleInputBlur = () => {
    setTimeout(() => setDropdownOpen(false), 150);
  };

  // The actual rooms to show (filtered)
  const roomsToShow =
    tab === "internal"
      ? internalRooms.filter((room) =>
          (room.name || room.roomId).toLowerCase().includes(search.trim().toLowerCase())
        )
      : externalRooms.filter((room) =>
          (room.name || room.roomId).toLowerCase().includes(search.trim().toLowerCase())
        );

  // If input is empty and dropdown open, show *all* rooms (unfiltered)
  const showAllRooms =
    search.trim().length === 0 && dropdownOpen
      ? tab === "internal"
        ? internalRooms
        : externalRooms
      : roomsToShow;

  React.useEffect(() => {
    // If dropdown closes, clear search for next time (optional)
    if (!dropdownOpen) setSearch("");
  }, [dropdownOpen]);

  // Find current selected room object
  const allRooms = internalRooms.concat(externalRooms);
  const selectedRoomObj = allRooms.find((room) => room.roomId === selectedRoom);
  const isExternal =
    selectedRoomObj?.isElementExternalVideoRoom && selectedRoomObj.isElementExternalVideoRoom();

  // The room link, for passing to Outlook etc. (not displayed)
  const currentRoomLink = selectedRoom ? getRoomPermalink(selectedRoom, isExternal) : null;

  return (
    <div className={css.container}>
      <TabList
        className={css.tabs}
        selectedValue={tab}
        onTabSelect={(_, d) => {
          setTab(d.value);
          setSelectedRoom(null); // clear selection when switching tabs
        }}
      >
        <Tab className={css.tab} id="externalTab" value="external">
          <img src="/assets/external.svg" alt="External Meeting" className={css.tabIcon} />
          External Meeting
        </Tab>

        <Tab className={css.tab} id="internalTab" value="internal">
          <img src="/assets/internal.svg" alt="Internal Meeting" className={css.tabIcon} />
          Internal Meeting
        </Tab>
      </TabList>
      <div className={css.tabPanel}>
        <Text weight="semibold" style={{ marginBottom: "8px", display: "block" }}>
          {tab === "internal" ? (
            "Internal meeting rooms are for participants who have access to your home server."
          ) : (
            <>
              External meeting rooms are for inviting participants who do not have access to your
              home server.
              <br />
              <br />
              <span style={{ fontWeight: 400 }}>
                To start the meeting, please use ‘For organizers: Start the meeting’.
              </span>
              <br />
            </>
          )}
        </Text>

        {/* Show spinner only above search bar */}
        {loading && (
          <div style={{ width: "100%", display: "flex", justifyContent: "center", padding: 10 }}>
            <Spinner size="small" label="Loading rooms…" />
          </div>
        )}

        {/* Search + Dropdown */}
        <div className={css.searchRow}>
          <Input
            ref={inputRef}
            value={search}
            onChange={(_, d) => setSearch(d.value)}
            placeholder="Search meeting rooms"
            contentAfter={<Search20Regular />}
            style={{ flex: 1 }}
            size="medium"
            disabled={
              loading ||
              (tab === "internal" ? internalRooms.length === 0 : externalRooms.length === 0)
            }
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            autoComplete="off"
          />
          {dropdownOpen && (
            <div className={css.dropdownList} style={{ top: inputHeight }}>
              {showAllRooms.length === 0 && !loading && (
                <div style={{ padding: "8px", color: "#999" }}>No rooms found</div>
              )}
              {showAllRooms.map((room) => {
                const isSelected = selectedRoom === room.roomId;
                // Don't show (External) label in external tab
                return (
                  <div
                    key={room.roomId}
                    className={`${css.option} ${isSelected ? css.selectedOption : ""}`}
                    onMouseDown={() => {
                      setSelectedRoom(room.roomId);
                      setSelectedRoomSource("existing");
                      setDropdownOpen(false);
                      const isExternalRoom =
                        room.isElementExternalVideoRoom && room.isElementExternalVideoRoom();
                      if (typeof onRoomLinkSelected === "function") {
                        if (isExternalRoom) {
                          onRoomLinkSelected({
                            publicLink: getRoomPermalink(room.roomId, true),
                            organizerLink: getRoomPermalink(room.roomId, false),
                            roomId: room.roomId,
                            isFromExisting: true,
                            roomName: room.name,
                          });
                        } else {
                          onRoomLinkSelected({
                            publicLink: getRoomPermalink(room.roomId, false),
                            roomId: room.roomId,
                            isFromExisting: true,
                            roomName: room.name,
                          });
                        }
                      }
                    }}
                    tabIndex={0}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        width: "100%",
                        minWidth: 0,
                      }}
                    >
                      {room.getJoinRule && room.getJoinRule() === "public" ? (
                        <span
                          style={{
                            fontSize: 22, // Similar to LockClosed20Filled size
                            width: 24,
                            height: 24,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "inherit",
                            flex: "0 0 auto",
                            fontWeight: 700,
                            opacity: 0.82, // Match Fluent UI icon subtlety
                          }}
                          aria-label="Public"
                        >
                          #
                        </span>
                      ) : (
                        <LockClosed20Filled
                          style={{ color: "inherit", width: 24, height: 24, flex: "0 0 auto" }}
                        />
                      )}

                      {/* Room name and ID, same line, ellipsis */}
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          minWidth: 0,
                          width: "100%",
                          gap: 8,
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            minWidth: 0,
                            maxWidth: 150,
                            flex: "1 1 0",
                          }}
                        >
                          {room.name || room.roomId}
                          {/* ---- Video Icon if video room (*/}
                          {(room?.getType?.() === RoomType.ElementVideo ||
                            room?.getType?.() === RoomType.ExternalVideoCall) && (
                            <Video24Filled
                              style={{
                                color: "#1777ab",
                                marginLeft: 7,
                                marginBottom: 2,
                                width: 16,
                                height: 16,
                                verticalAlign: "middle",
                              }}
                              title="Video Room"
                            />
                          )}
                        </span>
                        <span
                          style={{
                            color: "#888",
                            fontSize: "11px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            minWidth: 0,
                            maxWidth: 120,
                            flex: "0 1 0",
                          }}
                        >
                          {room.name ? room.roomId : ""}
                        </span>
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Show selected room banner after search, if picked from existing */}
        {selectedRoom && selectedRoomSource === "existing" && (
          <SelectedRoomBanner
            room={selectedRoomObj}
            roomId={selectedRoom}
            tab={tab}
            isExternal={isExternal}
            css={css}
            onClear={() => {
              setSelectedRoom(null);
              setSelectedRoomSource(null);
              if (typeof onRoomLinkSelected === "function") {
                onRoomLinkSelected({ publicLink: "", organizerLink: "", roomId: "" });
              }
            }}
            renderRoomIcon={renderRoomIcon}
            renderInfoBlock={renderInfoBlock}
          />
        )}

        {/* Create new video room */}
        <span className={css.createLabel}>Or create a new video room</span>
        {creating && (
          <div style={{ width: "100%", display: "flex", justifyContent: "center", padding: 10 }}>
            <Spinner size="small" label="Creating room…" />
          </div>
        )}

        <div className={css.createRow}>
          <Input
            className={css.wideInput}
            placeholder={"Enter room name"}
            value={newRoom}
            onChange={handleNewRoomInputChange}
            size="medium"
            disabled={creating}
            onKeyDown={(e) => {
              if (e.key === "Enter" && newRoom.trim() && !creating) handleCreateRoom();
            }}
            onFocus={handleNewRoomInputFocus}
            onBlur={handleNewRoomInputBlur}
          />
          <button
            className={css.plusBtnCircle}
            aria-label={
              selectedRoom && selectedRoomSource === "created" ? "Room created" : "Create room"
            }
            type="button"
            onMouseDown={handleCreateRoom} // instead of onClick!
            disabled={!newRoom.trim() || creating}
          >
            {selectedRoom && selectedRoomSource === "created" ? (
              <Checkmark24Filled className={css.plusIcon} />
            ) : (
              <span className={css.plusIcon}>+</span>
            )}
          </button>
        </div>

        {selectedRoomSource !== "created" && (
          <div className={css.infoBlock} style={{ marginTop: "8px", marginBottom: "0" }}>
            {tab === "internal" ? (
              <>
                <span>
                  {" "}
                  New room will be <b>public</b>. Anyone with access to your home server and the
                  meeting link can join. It will <b>not</b> appear in the explore public room list.
                </span>
              </>
            ) : (
              <>
                New room will be <b>private (external)</b>. You can invite additional meeting
                organizers from SalaX Secure Messaging room details.
              </>
            )}
          </div>
        )}

        {/* Show selected room banner after create, if created */}
        {selectedRoom && selectedRoomSource === "created" && (
          <SelectedRoomBanner
            room={selectedRoomObj}
            roomId={selectedRoom}
            tab={tab}
            isExternal={isExternal}
            css={css}
            onClear={() => {
              setSelectedRoom(null);
              setSelectedRoomSource(null);
              if (typeof onRoomLinkSelected === "function") {
                onRoomLinkSelected({ publicLink: "", organizerLink: "", roomId: "" });
              }
            }}
            renderRoomIcon={renderRoomIcon}
            renderInfoBlock={renderInfoBlock}
            source="created"
          />
        )}
      </div>
      {/* Footer */}
      <div className={css.footer}>
        <span className={css.user}>
          <span className={css.gear}>⚙️</span>
          Signed as {userEmail}
        </span>
        <span className={css.signOut} tabIndex={0} role="button" onClick={onSignOut}>
          Sign Out
        </span>
      </div>
    </div>
  );
}

interface SelectedRoomBannerProps {
  room: Room | undefined;
  roomId: string | null;
  tab: TabValue;
  isExternal: boolean;
  css: ReturnType<typeof useStyles>;
  onClear: () => void;
  renderRoomIcon: (room: Room | undefined) => React.ReactNode;
  renderInfoBlock: (room: Room | undefined, source?: "created" | "existing") => React.ReactNode;
  source?: "created" | "existing";
}

function SelectedRoomBanner({
  room,
  roomId,
  tab,
  isExternal,
  css,
  onClear,
  renderRoomIcon,
  renderInfoBlock,
  source = "existing",
}: SelectedRoomBannerProps) {
  if (!room && !roomId) return null;
  return (
    <>
      <div
        className={css.selectedBanner}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          marginTop: "10px",
          marginBottom: "8px",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", flex: 1 }}>
          {renderRoomIcon(room)}
          <span style={{ paddingLeft: 15 }}>{room?.name || roomId}</span>
          {tab === "internal" && isExternal && (
            <span style={{ marginLeft: 10, fontSize: 13, color: "#a54e00" }}>(External)</span>
          )}

          {(room?.getType?.() === RoomType.ElementVideo ||
            room?.getType?.() === RoomType.ExternalVideoCall) && (
            <Video24Filled
              style={{
                color: "#1777ab",
                marginLeft: 7,
                marginBottom: 0,
                width: 16,
                height: 16,
                verticalAlign: "middle",
              }}
              title="Video Room"
            />
          )}
        </span>
        <button
          type="button"
          aria-label="Clear selection"
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 0,
            marginLeft: 12,
            display: "flex",
            alignItems: "center",
          }}
          onClick={onClear}
          tabIndex={0}
        >
          <Dismiss20Regular style={{ color: "#777", width: 22, height: 22 }} />
        </button>
      </div>
      {renderInfoBlock(room, source)}
    </>
  );
}
